<script>
  import { slide } from "svelte/transition";
  import {
    Icon,
    ChevronRight,
    ChevronDown,
    FolderOpen,
    Folder,
    Document,
    DocumentCheck,
    BookOpen,
    ArrowRightEndOnRectangle,
    Clipboard,
    CheckCircle,
  } from "svelte-hero-icons";
  import InlineEdit from "$lib/Inputs/InlineEdit.svelte";
  import { filetree } from "../store";

  export let item;
  export let depth = 0;

  $: selectedNode = filetree.selectedNode;
  $: mainScriptId = filetree.mainScriptId;
  $: isSelected = $selectedNode?.id === item.id;
  $: isMoving = filetree.isMoving;
  $: isCopying = filetree.isCopying;
  $: isLibrary = item.meta?.type === "library";
  $: isMainScript = item.id === $mainScriptId;

  function copyId(event) {
    event.currentTarget.dispatchEvent(
      new CustomEvent("phx:clipcopy", {
        bubbles: true,
        detail: { text: item.id, type: "plaintext" },
      }),
    );
  }
  $: folderIcon = item.is_folder
    ? isLibrary
      ? BookOpen
      : item.expanded
        ? FolderOpen
        : Folder
    : isLibrary
      ? Document
      : isMainScript
        ? ArrowRightEndOnRectangle
        : DocumentCheck;

  // Calculate sticky top position based on depth
  // Toolbar is 32px tall, each folder row is ~32px
  $: stickyTop = `${(depth + 1) * 32}px`;

  function toggleExpand() {
    if (item.is_folder) {
      item.expanded = !item.expanded;
    }
  }

  function handleLabelChange(event) {
    filetree.updateItemLabel(item.id, event.detail);
  }

  function handleFolderClick() {
    if ($isMoving) {
      filetree.moveItem($selectedNode.id, item.id);
    } else if ($isCopying) {
      filetree.copyItem($selectedNode.id, item.id);
    } else {
      filetree.selectNode(item);
    }
  }
</script>

<div>
  <!-- svelte-ignore a11y-role-has-required-aria-props -->
  <!-- svelte-ignore a11y-interactive-supports-focus -->
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <div
    class="group flex items-center border border-transparent w-full rounded-md transition-colors duration-100 ease-in-out cursor-pointer"
    class:sticky={item.is_folder}
    class:bg-surface={item.is_folder && !isSelected}
    class:bg-action-soft={isSelected}
    class:border-action-outline={isSelected}
    style={item.is_folder ? `top: ${stickyTop}; z-index: ${9 - depth};` : ""}
    on:click={handleFolderClick}
    role="treeitem"
    aria-expanded={item.expanded}
  >
    <div class="flex items-center gap-x-1 py-1 px-1 w-full">
      <button
        class="{isSelected
          ? 'hover:bg-action-soft-hover focus:bg-action-soft-hover'
          : 'hover:bg-surface-hover focus:bg-surface-muted'} size-6 flex justify-center items-center rounded-md focus:outline-none disabled:opacity-50 disabled:pointer-events-none"
        on:click|stopPropagation={toggleExpand}
        disabled={!item.is_folder}
      >
        {#if item.is_folder}
          <Icon src={item.expanded ? ChevronDown : ChevronRight} class="w-4 h-4" />
        {/if}
      </button>

      <Icon
        src={folderIcon}
        class={`w-4 h-4 shrink-0 ${isLibrary ? "text-blue-500" : "text-content-subtle"}`}
      />

      <InlineEdit
        class="grow px-1 text-left rounded text-sm text-content-muted border-2 border-transparent "
        value={item.name}
        on:change={handleLabelChange}
      />

      {#if !item.is_folder}
        <button
          class="opacity-0 group-hover:opacity-100 flex items-center justify-center focus:outline-none focus:opacity-100 shrink-0"
          on:click|stopPropagation={copyId}
          title="Copy entrypoint ID"
        >
          <Icon
            src={Clipboard}
            class="copy-element w-4 h-4 text-content-subtle hover:text-content-muted"
          />
          <Icon src={CheckCircle} class="confirm-element hidden w-4 h-4 text-green-500" />
        </button>
      {/if}
    </div>
  </div>

  {#if item.expanded && item.children && item.children.length > 0}
    <div transition:slide={{ duration: 100 }} class="ml-4 pl-2 border-l border-line-strong">
      {#each item.children as child (child.id)}
        <svelte:self item={child} depth={depth + 1} />
      {/each}
    </div>
  {/if}
</div>

{#if false}
  <safelist class="bg-action-soft border border-action-outline hover:bg-action-soft-hover" />
{/if}
